import { businessDayOf, type PunchLike } from './domain.js';

// The « Pointages » attendance grid (operator, 2026-08-06): over a period
// longer than a day, one ROW per employee and one COLUMN per time bucket, each
// cell carrying the hours worked in it. The bucket follows the granularity —
// a week reads as its seven days, a month as its (possibly partial) weeks, a
// year as its twelve months — so the grid stays legible at every zoom instead
// of growing to 365 columns.
//
// A day column with no punch at all is « Repos »: that only means something
// per-day, so aggregated columns (week/month) show their total or nothing.
// Pure — the caller supplies the roster and renders the result.

export type AttendanceGranularity = 'day' | 'week' | 'month' | 'year';

export interface AttendanceColumn {
  /** Stable key — the first day of the bucket. */
  key: string;
  /** Column heading: 'lun. 3' · '3–9' · 'janv.' */
  label: string;
  /** Long form for the cell tooltip. */
  title: string;
  fromDay: string;
  toDay: string;
  /** True for a one-day bucket — only then can a cell read « Repos ». */
  isDay: boolean;
}

export interface AttendanceCell {
  hours: number;
  /** At least one punch in the bucket (worked, even if the hours round to 0). */
  hasPunch: boolean;
  /** Punches with no clock-out — a correction to make before payroll. */
  openCount: number;
  /** Days in the bucket with at least one punch. */
  daysWorked: number;
  /** False when the employee was under no contract for the WHOLE bucket — renders blank, not « Repos ». */
  inContract: boolean;
}

export interface AttendanceRow {
  employeeId: string;
  cells: AttendanceCell[];
  totalHours: number;
  daysWorked: number;
  openCount: number;
}

/** The contract span to reckon « Repos » against; open-ended on either side. */
export interface AttendanceContract {
  startDate?: string | null;
  endDate?: string | null;
}

const addDays = (day: string, delta: number): string => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

const atNoon = (day: string): Date => new Date(`${day}T12:00:00`);

/** Monday of the week containing `day` (ISO week). */
const mondayOf = (day: string): string => {
  const d = atNoon(day);
  return addDays(day, -((d.getDay() + 6) % 7));
};

const fmtDay = (day: string): string =>
  atNoon(day).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' });

const fmtLong = (day: string): string =>
  atNoon(day).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

/**
 * The column buckets of a period. `fromDay`/`toDay` are the period's own
 * bounds, so month weeks are CLAMPED to the month: a month starting on a
 * Wednesday opens with a 5-day partial week rather than reaching back into the
 * previous month, and the columns always sum to exactly the period.
 */
export function attendanceColumns(
  granularity: AttendanceGranularity,
  fromDay: string,
  toDay: string,
): AttendanceColumn[] {
  const columns: AttendanceColumn[] = [];
  if (fromDay > toDay) return columns;

  // A day and a week are both drawn as day columns — the day simply has one.
  // That is what lets the Jour view reuse the week's grid unchanged.
  if (granularity === 'day' || granularity === 'week') {
    for (let day = fromDay; day <= toDay; day = addDays(day, 1)) {
      columns.push({ key: day, label: fmtDay(day), title: fmtLong(day), fromDay: day, toDay: day, isDay: true });
    }
    return columns;
  }

  if (granularity === 'month') {
    let start = fromDay;
    while (start <= toDay) {
      // End of THIS week (Sunday), clipped to the period — hence the partials.
      const sunday = addDays(mondayOf(start), 6);
      const end = sunday > toDay ? toDay : sunday;
      columns.push({
        key: start,
        label: start === end ? `${Number(start.slice(8))}` : `${Number(start.slice(8))}–${Number(end.slice(8))}`,
        title: `Du ${fmtLong(start)} au ${fmtLong(end)}`,
        fromDay: start,
        toDay: end,
        isDay: start === end,
      });
      start = addDays(end, 1);
    }
    return columns;
  }

  // Year — twelve month buckets, clipped to the period at both ends.
  let start = fromDay;
  while (start <= toDay) {
    const y = Number(start.slice(0, 4));
    const m = Number(start.slice(5, 7));
    const monthEnd = `${start.slice(0, 7)}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
    const end = monthEnd > toDay ? toDay : monthEnd;
    columns.push({
      key: start,
      label: atNoon(start).toLocaleDateString('fr-FR', { month: 'short' }),
      title: atNoon(start).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
      fromDay: start,
      toDay: end,
      isDay: false,
    });
    start = addDays(end, 1);
  }
  return columns;
}

/** Per (business day, employee): hours, punch count, open count. */
function bucketByDay(
  entries: PunchLike[],
): Map<string, Map<string, { hours: number; punches: number; open: number }>> {
  const byDay = new Map<string, Map<string, { hours: number; punches: number; open: number }>>();
  for (const entry of entries) {
    const day = businessDayOf(new Date(entry.clockIn));
    const byEmployee = byDay.get(day) ?? new Map();
    const cur = byEmployee.get(entry.employeeId) ?? { hours: 0, punches: 0, open: 0 };
    // An open shift contributes no hours — it is flagged instead, exactly as
    // computeTimesheet does, so the grid and the payroll report agree.
    cur.hours = round2(
      cur.hours +
        (entry.clockOut === null ? 0 : (Date.parse(entry.clockOut) - Date.parse(entry.clockIn)) / 3_600_000),
    );
    cur.punches += 1;
    if (entry.clockOut === null) cur.open += 1;
    byEmployee.set(entry.employeeId, cur);
    byDay.set(day, byEmployee);
  }
  return byDay;
}

const coversAny = (contract: AttendanceContract | undefined, fromDay: string, toDay: string): boolean => {
  if (!contract) return true;
  if (contract.startDate && contract.startDate > toDay) return false;
  if (contract.endDate && contract.endDate < fromDay) return false;
  return true;
};

/**
 * Fill the grid. `employees` is the row set and its order is preserved — the
 * caller decides the ordering (« Pointages » groups by team). `contracts` is
 * optional per employee: without it every column counts as in-contract.
 */
export function computeAttendanceMatrix(
  entries: PunchLike[],
  employees: { id: string; contract?: AttendanceContract }[],
  columns: AttendanceColumn[],
): AttendanceRow[] {
  const byDay = bucketByDay(entries);

  return employees.map((employee) => {
    const cells = columns.map((column) => {
      let hours = 0;
      let punches = 0;
      let openCount = 0;
      let daysWorked = 0;
      for (let day = column.fromDay; day <= column.toDay; day = addDays(day, 1)) {
        const hit = byDay.get(day)?.get(employee.id);
        if (!hit) continue;
        hours += hit.hours;
        punches += hit.punches;
        openCount += hit.open;
        daysWorked += 1;
      }
      return {
        hours: round2(hours),
        hasPunch: punches > 0,
        openCount,
        daysWorked,
        inContract: coversAny(employee.contract, column.fromDay, column.toDay),
      };
    });

    return {
      employeeId: employee.id,
      cells,
      totalHours: round2(cells.reduce((sum, c) => sum + c.hours, 0)),
      daysWorked: cells.reduce((sum, c) => sum + c.daysWorked, 0),
      openCount: cells.reduce((sum, c) => sum + c.openCount, 0),
    };
  });
}
